class HeaderCategoriesMenu extends HTMLElement {
  connectedCallback() {
    this.#trigger = this.querySelector('[data-categories-toggle]');
    this.#panel = this.querySelector('[data-categories-panel]');
    if (!this.#trigger || !this.#panel) return;

    this.#trigger.addEventListener('click', this.#onToggleClick);
    document.addEventListener('click', this.#onDocumentClick);
    document.addEventListener('keydown', this.#onDocumentKeydown);

    this.querySelectorAll('[data-categories-item][data-level="1"]').forEach((item) => {
      item.addEventListener('mouseenter', this.#onLevel1Enter);
      item.addEventListener('focusin', this.#onLevel1Enter);
    });

    this.querySelectorAll('[data-categories-item][data-level="2"]').forEach((item) => {
      item.addEventListener('mouseenter', this.#onLevel2Enter);
      item.addEventListener('focusin', this.#onLevel2Enter);
    });
  }

  disconnectedCallback() {
    this.#trigger?.removeEventListener('click', this.#onToggleClick);
    document.removeEventListener('click', this.#onDocumentClick);
    document.removeEventListener('keydown', this.#onDocumentKeydown);
  }

  /** @type {HTMLButtonElement | null} */
  #trigger = null;

  /** @type {HTMLElement | null} */
  #panel = null;

  #onToggleClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.#setOpen(!this.classList.contains('is-open'));
  };

  #onDocumentClick = (event) => {
    if (!(event.target instanceof Node)) return;
    if (!this.classList.contains('is-open')) return;
    if (this.contains(event.target)) return;
    this.#setOpen(false);
  };

  #onDocumentKeydown = (event) => {
    if (event.key === 'Escape' && this.classList.contains('is-open')) {
      this.#setOpen(false);
      this.#trigger?.focus();
    }
  };

  #onLevel1Enter = (event) => {
    const item = event.currentTarget;
    if (!(item instanceof HTMLElement)) return;

    const index = item.getAttribute('data-index');
    if (index == null) return;

    this.#setActiveItem(this.querySelectorAll('[data-categories-item][data-level="1"]'), item);

    const level2Column = this.querySelector('[data-categories-col="2"]');
    if (!(level2Column instanceof HTMLElement)) return;

    level2Column.querySelectorAll('[data-categories-panel-l1]').forEach((panel) => {
      if (!(panel instanceof HTMLElement)) return;
      const isMatch = panel.getAttribute('data-categories-panel-l1') === index;
      panel.hidden = !isMatch;
      panel.classList.toggle('is-active', isMatch);
    });

    const firstLevel2 = level2Column.querySelector(
      `[data-categories-panel-l1="${index}"] [data-categories-item][data-level="2"]`
    );
    if (firstLevel2 instanceof HTMLElement) {
      firstLevel2.dispatchEvent(new Event('mouseenter', { bubbles: false }));
    } else {
      this.#hideLevel3();
    }
  };

  #onLevel2Enter = (event) => {
    const item = event.currentTarget;
    if (!(item instanceof HTMLElement)) return;

    const l1 = item.getAttribute('data-parent-l1');
    const index = item.getAttribute('data-index');
    if (l1 == null || index == null) return;

    const panel = item.closest('[data-categories-panel-l1]');
    if (panel instanceof HTMLElement) {
      panel.querySelectorAll('[data-categories-item][data-level="2"]').forEach((el) => {
        el.classList.toggle('is-active', el === item);
      });
    }

    const level3Column = this.querySelector('[data-categories-col="3"]');
    if (!(level3Column instanceof HTMLElement)) return;

    const panelKey = `${l1}-${index}`;
    let hasMatch = false;

    level3Column.querySelectorAll('[data-categories-panel-l2]').forEach((panelEl) => {
      if (!(panelEl instanceof HTMLElement)) return;
      const isMatch = panelEl.getAttribute('data-categories-panel-l2') === panelKey;
      panelEl.hidden = !isMatch;
      panelEl.classList.toggle('is-active', isMatch);
      if (isMatch) hasMatch = true;
    });

    if (!hasMatch) {
      this.#hideLevel3();
    }
  };

  #hideLevel3() {
    const level3Column = this.querySelector('[data-categories-col="3"]');
    level3Column?.querySelectorAll('[data-categories-panel-l2]').forEach((panel) => {
      if (panel instanceof HTMLElement) {
        panel.hidden = true;
        panel.classList.remove('is-active');
      }
    });
  }

  /**
   * @param {NodeListOf<Element>} items
   * @param {HTMLElement} active
   */
  #setActiveItem(items, active) {
    items.forEach((el) => {
      if (el instanceof HTMLElement) {
        el.classList.toggle('is-active', el === active);
      }
    });
  }

  /** @param {boolean} open */
  #setOpen(open) {
    this.classList.toggle('is-open', open);
    this.#trigger?.setAttribute('aria-expanded', open ? 'true' : 'false');

    if (!this.#panel) return;

    if (open) {
      this.#panel.hidden = false;
    } else {
      this.#panel.hidden = true;
    }
  }
}

if (!customElements.get('header-categories-menu')) {
  customElements.define('header-categories-menu', HeaderCategoriesMenu);
}
